import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { accountsApi, UpdateAccountData, PermissionGroupOption, Account, Operator } from '../api/accountsApi';
import { buildingsApi, Building } from '../api/buildingsApi';
import { getApiErrorMessage } from '../utils/apiErrors';

export const EditAccount: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [account, setAccount] = useState<Account | null>(null);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroupOption[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [applyAllBuildings, setApplyAllBuildings] = useState(false);
  const [formData, setFormData] = useState<UpdateAccountData>({
    name: '',
    email: '',
    phoneNumber: '',
    username: '',
    role: '',
    isActive: true,
    permissionGroupId: '',
    operatorId: '',
  });

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoadingData(true);
      const [accountData, operatorsData, buildingsData] = await Promise.all([
        accountsApi.getById(id),
        isSuperAdmin ? accountsApi.getOperators() : Promise.resolve([]),
        buildingsApi.getBuildings(),
      ]);

      setAccount(accountData);
      setOperators(operatorsData);
      setBuildings(buildingsData);

      // Initialize form data
      setFormData({
        name: accountData.name,
        email: accountData.email,
        phoneNumber: accountData.phoneNumber || '',
        username: accountData.username,
        role: accountData.role || '',
        isActive: accountData.isActive,
        permissionGroupId: accountData.permissionGroup?.id || '',
        operatorId: accountData.operatorId || '',
        buildingIds: (accountData as any).buildingIds || [],
      });

      // Load permission groups for the account's role
      if (accountData.role === 'SUPERADMIN' || accountData.role === 'STAFF') {
        const groupsData = await accountsApi.getPermissionGroups(
          accountData.role,
          accountData.role === 'STAFF' ? accountData.operatorId : undefined,
        );
        setPermissionGroups(groupsData);
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      alert(getApiErrorMessage(error, t('errorLoadingAccounts')));
      navigate('/accounts');
    } finally {
      setLoadingData(false);
    }
  };

  const loadPermissionGroups = async (role: string, operatorId?: string) => {
    try {
      // Load permission groups for SUPERADMIN and STAFF roles
      if (role === 'SUPERADMIN' || role === 'STAFF') {
        const groupsData = await accountsApi.getPermissionGroups(role, operatorId);
        setPermissionGroups(groupsData);
      } else {
        // Clear permission groups for other roles
        setPermissionGroups([]);
      }
    } catch (error) {
      console.error('Error loading permission groups:', error);
      setPermissionGroups([]);
    }
  };

  const handleInputChange = (field: keyof UpdateAccountData, value: string | boolean) => {
    setFormData((prev: UpdateAccountData) => {
      const newData = { ...prev, [field]: value };
      
      // Clear permission group and operator when role changes
      if (field === 'role') {
        newData.permissionGroupId = '';
        newData.operatorId = '';
        
        if (typeof value === 'string' && value !== 'STAFF') {
          loadPermissionGroups(value);
        }
      }

      if (field === 'operatorId') {
        newData.permissionGroupId = '';
        if (typeof value === 'string' && newData.role === 'STAFF') {
          loadPermissionGroups('STAFF', value);
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      alert(t('pleaseEnterFullName'));
      return;
    }
    if (!formData.email?.trim()) {
      alert(t('pleaseEnterEmail'));
      return;
    }
    if (!formData.username?.trim()) {
      alert(t('pleaseEnterUsername'));
      return;
    }
    if (!formData.role) {
      alert(t('pleaseSelectRole'));
      return;
    }

    // Role-specific validation
    if (formData.role === 'STAFF') {
      if (!formData.permissionGroupId) {
        alert(t('pleaseSelectPermissionGroup'));
        return;
      }
      if (isSuperAdmin && !formData.operatorId) {
        alert(t('pleaseSelectOperator'));
        return;
      }
    }

    if (!id) return;

    try {
      setLoading(true);
      const submitData = { ...formData };
      if (formData.role === 'STAFF') {
        const selected = (formData.buildingIds as string[] | undefined) || [];
        submitData.buildingIds = applyAllBuildings ? buildings.map(b => b.id) : selected;
      } else {
        delete submitData.buildingIds;
      }
      
      // Clean up data based on role
      if (formData.role === 'OPERATOR' || formData.role === 'RESIDENT') {
        delete submitData.permissionGroupId;
        delete submitData.operatorId;
      } else if (formData.role === 'SUPERADMIN') {
        // For SUPERADMIN, don't send operatorId (should be null)
        delete submitData.operatorId;
        submitData.permissionGroupId = formData.permissionGroupId || null;
      } else if (formData.role === 'STAFF' && !isSuperAdmin) {
        // For OPERATOR editing STAFF, don't send operatorId (backend will handle)
        delete submitData.operatorId;
      }
      
      await accountsApi.update(id, submitData);
      alert(t('accountUpdatedSuccess'));
      navigate('/accounts');
    } catch (error: any) {
      alert(getApiErrorMessage(error, t('errorUpdatingAccount')));
    } finally {
      setLoading(false);
    }
  };

  const handleBuildingIdsChange = (ids: string[]) => {
    setFormData((prev: UpdateAccountData) => ({
      ...prev,
      buildingIds: ids,
    }));
  };

  const shouldShowPermissionGroup = () => {
    return formData.role === 'SUPERADMIN' || formData.role === 'STAFF';
  };

  const shouldShowOperatorSelection = () => {
    return formData.role === 'STAFF' && isSuperAdmin;
  };

  const shouldShowBuildingSelection = () => {
    return formData.role === 'STAFF';
  };

  if (loadingData) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Account not found</h3>
          <button
            onClick={() => navigate('/accounts')}
            className="text-cyan-600 hover:text-cyan-700"
          >
            Back to Accounts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>{t('accountManagement')}</span> / 
          <span className="text-cyan-600 mx-1">{t('accountList')}</span> / 
          <span className="text-gray-900">{t('edit')} "{account.name}"</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Account</h1>
        <p className="text-gray-600">Update account information and access settings</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - General Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('generalInfo')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fullName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterFullName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterEmail')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('phoneNumber')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber || ''}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterPhoneNumber')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('roleSelection')} *
                  </label>
                  <select
                    value={formData.role || ''}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('selectRole')}</option>
                    {isSuperAdmin && (
                      <>
                        <option value="SUPERADMIN">{t('SUPERADMIN')}</option>
                        <option value="OPERATOR">{t('OPERATOR')}</option>
                      </>
                    )}
                    <option value="STAFF">{t('STAFF')}</option>
                    <option value="RESIDENT">{t('RESIDENT')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('statusSelection')}
                  </label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isActive"
                        value="true"
                        checked={formData.isActive === true}
                        onChange={() => handleInputChange('isActive', true)}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('active')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isActive"
                        value="false"
                        checked={formData.isActive === false}
                        onChange={() => handleInputChange('isActive', false)}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('inactive')}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Login Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('loginInformation')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('username')} *
                  </label>
                  <input
                    type="text"
                    value={formData.username || ''}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterUsername')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('password')}
                  </label>
                  <input
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterPassword')}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password</p>
                </div>

                {/* Operator Selection - Only for STAFF when SUPERADMIN is editing */}
                {shouldShowOperatorSelection() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('operator')} *
                    </label>
                    <select
                      value={formData.operatorId || ''}
                      onChange={(e) => handleInputChange('operatorId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required
                    >
                      <option value="">{t('selectOperator')}</option>
                      {operators.map((operator) => (
                        <option key={operator.id} value={operator.id}>
                          {operator.name} ({operator.email})
                        </option>
                      ))}
                    </select>
                    {account.operator && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t('currentOperator')}: {account.operator.name} ({account.operator.email})
                      </p>
                    )}
                  </div>
                )}

                {/* Permission Group - Only for STAFF */}
                {shouldShowPermissionGroup() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('permissionGroup')}
                      {formData.role === 'STAFF' && ' *'}
                    </label>
                    <select
                      value={formData.permissionGroupId || ''}
                      onChange={(e) => handleInputChange('permissionGroupId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required={formData.role === 'STAFF'}
                    >
                      {formData.role === 'SUPERADMIN' ? (
                        <option value="">{t('fullSystemAccessNoGroup')}</option>
                      ) : (
                        <option value="">{t('selectPermissionGroup')}</option>
                      )}
                      {permissionGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                          {group.description && ` - ${group.description}`}
                        </option>
                      ))}
                    </select>
                    {formData.role === 'SUPERADMIN' && (
                      <p className="text-xs text-gray-500 mt-1">{t('superadminPermissionGroupHelp')}</p>
                    )}
                  </div>
                )}

                {/* Building assignment - for STAFF users (Permission Group style) */}
                {shouldShowBuildingSelection() && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-2">
                    <h3 className="text-md font-semibold text-gray-900 mb-3">{t('applyBuildings')}</h3>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={applyAllBuildings}
                          onChange={() => setApplyAllBuildings(true)}
                          className="text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{t('allBuildings')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!applyAllBuildings}
                          onChange={() => setApplyAllBuildings(false)}
                          className="text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{t('selectBuildings')}</span>
                      </label>
                    </div>
                    {!applyAllBuildings && (
                      <div className="mt-4">
                        {buildings.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 mb-3">{t('selectBuildingsFromList')}:</p>
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                              {buildings.map((b) => {
                                const checked = (formData.buildingIds || []).includes(b.id);
                                return (
                                  <label key={b.id} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = new Set(formData.buildingIds || []);
                                        if (e.target.checked) next.add(b.id); else next.delete(b.id);
                                        handleBuildingIdsChange(Array.from(next));
                                      }}
                                      className="text-cyan-600 focus:ring-cyan-500 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{b.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 text-sm text-gray-600">{t('noBuildings')}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Account Statistics */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`font-medium ${account.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {account.isActive ? t('active') : t('inactive')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('lastLogin')}:</span>
                  <span className="font-medium text-gray-900">
                    {account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleDateString() : t('never')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('created')}:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(account.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {account.operator && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('operator')}:</span>
                    <span className="font-medium text-gray-900">
                      {account.operator.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/accounts')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all duration-200"
          >
            {loading ? t('updating') : 'Update Account'}
          </button>
        </div>
      </form>
    </div>
  );
}; 